/**
 * Flint Product Hero - JavaScript  
 * Flint Fires: Igniting Luxury Living
 * Handles gallery interactions, variant selection, and dynamic updates
 */

console.log('🔥 FLINT HERO JS FILE LOADED!');
console.log('🔥 This should appear immediately if file loads');

class FlintProductHero {
  constructor(container) {
    this.container = container;
    this.gallery = container.querySelector('.flint-hero__gallery');
    this.counterCurrent = container.querySelector('.flint-hero__counter-current');
    this.counterTotal = container.querySelector('.flint-hero__counter-total');
    // Look for form in both the main container and content wrapper
    this.form = container.querySelector('.flint-hero__form') || document.querySelector('.flint-product-hero-content .flint-hero__form');
    
    this.flickityInstance = null;
    
    this.init();
  }

  init() {
    // Wait for Flickity to load
    this.waitForFlickity().then(() => {
      this.initGallery();
      this.initVariantSelectors();
      this.initAccessibility();
      this.initCartForm();
    });
  }

  /**
   * Wait for Flickity library to load
   */
  waitForFlickity() {
    return new Promise((resolve) => {
      const checkFlickity = () => {
        if (typeof Flickity !== 'undefined') {
          resolve();
        } else {
          setTimeout(checkFlickity, 50);
        }
      };
      checkFlickity();
    });
  }

  /**
   * Initialize Flickity Gallery
   */
  initGallery() {
    if (!this.gallery) return;

    // Skip Flickity for static galleries (1-3 images) - they use native scroll on mobile
    const isStaticGallery = this.gallery.classList.contains('flint-hero__gallery--single-static');
    if (isStaticGallery) {
      console.log('🔥 Static gallery (1-3 images) - using native scroll, skipping Flickity');
      return;
    }

    // Initialize Flickity only for 4+ images
    this.flickityInstance = Flickity.data(this.gallery);
    
    if (!this.flickityInstance) {
      console.log('🔥 Creating new Flickity instance for 4+ images');
      this.flickityInstance = new Flickity(this.gallery, {
        cellAlign: 'left',
        contain: true,
        wrapAround: false,
        pageDots: false,
        prevNextButtons: false,
        draggable: true,
        freeScroll: true
      });
    }

    if (this.flickityInstance) {
      // Let Flickity handle its own cursor styles naturally
      
      // Set data attribute for cell count (for CSS centering)
      const cellCount = this.flickityInstance.cells.length;
      this.gallery.setAttribute('data-flickity-count', cellCount);
      
      // Update counter on slide change
      this.flickityInstance.on('change', (index) => {
        this.updateCounter(index + 1);
      });

      // Keyboard navigation
      this.gallery.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          this.flickityInstance.previous();
        } else if (e.key === 'ArrowRight') {
          this.flickityInstance.next();
        }
      });

      console.log(' Gallery initialized');
    } 
    this.addTouchFeedback();
  }

  /**
   * Update image counter
   */
  updateCounter(currentIndex) {
    if (this.counterCurrent) {
      this.counterCurrent.textContent = currentIndex;
      
      // Add animation
      this.counterCurrent.style.transform = 'scale(1.2)';
      setTimeout(() => {
        this.counterCurrent.style.transform = 'scale(1)';
      }, 150);
    }
  }

  /**
   * Add touch feedback for better UX
   */
  addTouchFeedback() {
    const cells = this.gallery.querySelectorAll('.flint-hero__gallery-cell');
    
    cells.forEach(cell => {
      cell.addEventListener('touchstart', () => {
        cell.style.opacity = '0.9';
      });
      
      cell.addEventListener('touchend', () => {
        cell.style.opacity = '1';
      });
    });
  }

  /**
   * Initialize variant selectors
   */
  initVariantSelectors() {
    if (!this.form) return;

    // Swatch inputs (radio buttons)
    const swatchInputs = this.form.querySelectorAll('.flint-hero__swatch-input');
    swatchInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.handleVariantChange();
      });
    });

    // Dropdown selects
    const selects = this.form.querySelectorAll('.flint-hero__select');
    selects.forEach(select => {
      select.addEventListener('change', () => {
        this.handleVariantChange();
      });
    });

    // Initialize with current selection
    this.handleVariantChange();
  }

  /**
   * Handle variant selection change
   * Updates price, availability, and image if needed
   */
  handleVariantChange() {
    // Get selected options
    const selectedOptions = this.getSelectedOptions();
    
    // Find matching variant (if using Shopify variant system)
    // This is a placeholder - actual implementation depends on product structure
    console.log('Selected options:', selectedOptions);
    
    // You would typically:
    // 1. Find the matching variant
    // 2. Update the price
    // 3. Update availability
    // 4. Update the main image if variant has a specific image
    
    // Example:
    // const variant = this.findVariant(selectedOptions);
    // if (variant) {
    //   this.updatePrice(variant.price);
    //   this.updateAvailability(variant.available);
    //   if (variant.featured_image) {
    //     this.updateMainImage(variant.featured_image);
    //   }
    // }
  }

  /**
   * Get currently selected variant options
   */
  getSelectedOptions() {
    const options = {};
    
    // Get swatch selections
    const swatchInputs = this.form.querySelectorAll('.flint-hero__swatch-input:checked');
    swatchInputs.forEach(input => {
      const name = input.name.replace('options[', '').replace(']', '');
      options[name] = input.value;
    });
    
    // Get dropdown selections
    const selects = this.form.querySelectorAll('.flint-hero__select');
    selects.forEach(select => {
      const name = select.name.replace('options[', '').replace(']', '');
      options[name] = select.value;
    });
    
    return options;
  }

  /**
   * Initialize cart form - Clean and simple approach
   */
  initCartForm() {
    if (!this.form) return;
    
    console.log('🎯 Setting up simple AJAX cart');
    
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      console.log('🎯 Form submitted - handling via AJAX');
      
      const formData = new FormData(this.form);
      const variantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity') || '1');
      
      if (!variantId) {
        console.error('🎯 No variant ID found');
        return;
      }
      
      // Simple AJAX cart add
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity })
      })
      .then(response => response.json())
      .then(data => {
        console.log('🎯 Cart add successful', data);
        
        // CRITICAL FIX: Refresh cart drawer content from server
        // This ensures empty cart gets updated HTML structure
        setTimeout(() => {
          this.refreshCartDrawerContent();
        }, 300);
        
        // Open drawer after content is refreshed
        setTimeout(() => {
          this.openCartDrawer();
          
          // Update counter after drawer opens
          setTimeout(() => {
            this.waitForCartCounterAndUpdate();
          }, 400);
        }, 800);
      })
      .catch(error => {
        console.error('🎯 Cart add failed:', error);
      });
    });
  }

  /**
   * CRITICAL FIX: Wait for cart counter element to exist, then update
   * Solves the delay issue where counter doesn't exist initially
   */
  waitForCartCounterAndUpdate() {
    const maxAttempts = 20; // Try for up to 2 seconds
    let attempts = 0;
    
    const checkAndUpdate = () => {
      const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count], .cart-bubble__text-count, [data-testid="cart-bubble"]');
      
      if (cartCountElements.length > 0) {
        console.log('🎯 Cart counter element found, updating now');
        this.updateCartCount();
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkAndUpdate, 100); // Check every 100ms
      } else {
        console.log('🎯 Cart counter element not found after waiting');
      }
    };
    
    // Start checking immediately
    checkAndUpdate();
  }

  /**
   * CRITICAL FIX: Refresh cart drawer HTML from server
   * This solves the empty cart bug by fetching updated drawer content
   * 
   * WHY THIS IS NEEDED:
   * When cart is empty, theme's drawer HTML has minimal content.
   * After adding first item via AJAX, we need fresh HTML with proper structure.
   * 
   * IMPORTANT: Fresh HTML has hidden counter (visually-hidden on parent),
   * so we fix visibility immediately after refresh (see setTimeout below).
   */
  refreshCartDrawerContent() {
    console.log('🎯 Refreshing cart drawer content from server');
    
    // Find the cart drawer element
    const cartDrawer = document.querySelector('cart-drawer, .cart-drawer, [data-cart-drawer]');
    if (!cartDrawer) {
      console.log('🎯 No cart drawer found in DOM');
      return;
    }
    
    // Fetch the full page HTML to get updated cart drawer
    fetch(window.location.href)
      .then(response => response.text())
      .then(html => {
        // Parse the HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Find the cart drawer in the fetched HTML
        const newCartDrawer = doc.querySelector('cart-drawer, .cart-drawer, [data-cart-drawer]');
        
        if (newCartDrawer) {
          // Replace the entire cart drawer with the updated version
          cartDrawer.innerHTML = newCartDrawer.innerHTML;
          console.log('🎯 Cart drawer content refreshed successfully');
          
          // ========================================================================
          // CRITICAL FIX: Update counter IMMEDIATELY after drawer refresh
          // ========================================================================
          // 
          // THE PROBLEM:
          // Fresh HTML from server has TWO hiding mechanisms:
          // 1. Parent <div class="cart-bubble visually-hidden"> 
          // 2. Child <span class="cart-bubble__text-count hidden">0</span>
          //
          // THE SOLUTION:
          // 1. Fetch cart count first
          // 2. Update counter VALUE before showing (prevents "0" flash)
          // 3. Remove hiding classes from BOTH parent and child
          // 4. Force visibility with inline styles
          //
          // WHY IT WORKS:
          // By setting the correct value BEFORE making visible, user never sees "0"
          // ========================================================================
          setTimeout(() => {
            console.log('🎯 Updating counter after drawer refresh');
            
            // Get the cart count first before showing
            fetch('/cart.js')
              .then(response => response.json())
              .then(cart => {
                // Fix the counter bubble parent (has visually-hidden class)
                const cartBubbles = document.querySelectorAll('.cart-bubble');
                cartBubbles.forEach(bubble => {
                  bubble.classList.remove('visually-hidden');
                  bubble.classList.remove('hidden');
                });
                
                // Fix and UPDATE the counter elements at the same time
                const counterElements = document.querySelectorAll('.cart-count, [data-cart-count], .cart-bubble__text-count, [data-testid="cart-bubble"]');
                counterElements.forEach(el => {
                  // Update the value FIRST
                  el.textContent = cart.item_count;
                  // THEN make it visible (no flash of "0")
                  el.classList.remove('hidden');
                  el.style.display = 'inline-block';
                  el.style.visibility = 'visible';
                  el.style.opacity = '1';
                });
                
                console.log('🎯 Counter updated and shown with correct value:', cart.item_count);
              })
              .catch(err => console.log('🎯 Counter quick update failed:', err));
          }, 50);
        } else {
          console.log('🎯 Could not find cart drawer in fetched HTML');
        }
      })
      .catch(error => {
        console.error('🎯 Error refreshing cart drawer:', error);
      });
  }

  /**
   * Update cart drawer quantity inputs to match actual cart
   */
  updateCartDrawerQuantities() {
    console.log('🎯 Updating cart drawer quantities');
    
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const quantityInputs = document.querySelectorAll('input[name*="quantity"], input[type="number"], .quantity__input');
        quantityInputs.forEach(input => {
          // Check if this input is in the cart drawer
          const isInDrawer = input.closest('.cart-drawer, [data-cart-drawer], cart-drawer');
          if (isInDrawer) {
            // Find the variant ID for this cart item
            const cartItem = input.closest('[data-variant-id], .cart-item, [data-cart-item]');
            if (cartItem) {
              const variantId = cartItem.dataset.variantId || 
                              cartItem.querySelector('[data-variant-id]')?.dataset.variantId ||
                              cartItem.querySelector('input[name="id"]')?.value;
              
              if (variantId) {
                const cartLine = cart.items.find(item => item.variant_id == variantId);
                if (cartLine) {
                  console.log(`🎯 Updating drawer quantity to ${cartLine.quantity} for variant ${variantId}`);
                  input.value = cartLine.quantity;
                }
              }
            }
          }
        });
      })
      .catch(err => console.error('🎯 Cart drawer update failed:', err));
  }

  /**
   * Show add to cart success state (AutoMazen style)
   */
  showAddToCartSuccess(button) {
    const originalText = button.textContent;
    button.textContent = 'Added to Cart!';
    button.style.background = '#4caf50';
    
    setTimeout(() => {
      button.textContent = originalText;
      button.style.background = '';
    }, 2000);
  }

  /**
   * Show add to cart error state (AutoMazen style)
   */
  showAddToCartError(button) {
    const originalText = button.textContent;
    button.textContent = 'Error - Try Again';
    button.style.background = '#f44336';
    
    setTimeout(() => {
      button.textContent = originalText;
      button.style.background = '';
    }, 3000);
  }

  /**
   * Update cart count - simplified like AutoMazen
   */
  triggerCartUpdate(data) {
    // Simple approach: just update cart count (like AutoMazen)
    this.updateCartCount();
    
    // Optional: trigger basic cart events
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { item: data }
    }));
  }

  /**
   * Update cart count in header
   * 
   * REDUNDANT SAFETY NET:
   * This method is called after drawer opens (with delay) to ensure counter is visible.
   * Even though we fix visibility in refreshCartDrawerContent(), this provides backup
   * in case timing is off or elements get re-rendered.
   * 
   * Also removes visually-hidden from parent .cart-bubble element.
   */
  updateCartCount() {
    console.log('🔥 updateCartCount() METHOD CALLED!');
    console.log('🎯 Looking for cart count elements...');
    // Look for various cart count selectors (including the cart bubble)
    const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count], .cart-bubble__text-count, [data-testid="cart-bubble"]');
    console.log('🎯 Found cart count elements:', cartCountElements.length);
    
    if (cartCountElements.length > 0) {
      console.log('🎯 Fetching cart data from /cart.js');
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          console.log('🎯 Cart API response:', cart.item_count, 'items');
          
          // CRITICAL FIX: Remove visually-hidden from parent cart-bubble
          if (cart.item_count > 0) {
            const cartBubbles = document.querySelectorAll('.cart-bubble');
            cartBubbles.forEach(bubble => {
              bubble.classList.remove('visually-hidden');
              bubble.classList.remove('hidden');
              console.log('🎯 Removed visually-hidden from cart bubble parent');
            });
          }
          
          // Update all cart count elements
          cartCountElements.forEach((element, index) => {
            console.log(`🎯 Updating element ${index}:`, element.className, 'from', element.textContent, 'to', cart.item_count);
            element.textContent = cart.item_count;
            if (cart.item_count > 0) {
              // CRITICAL FIX: Force visibility multiple ways
              element.style.display = 'inline-block';
              element.style.visibility = 'visible';
              element.style.opacity = '1';
              // Remove ALL hiding classes
              element.classList.remove('hidden');
              element.classList.remove('hide');
              element.classList.remove('invisible');
              console.log(`🎯 Element ${index} visibility forced, classes after:`, element.className);
            }
          });
          
          console.log('🎯 Cart count update complete');
        })
        .catch(err => console.log('🎯 Cart count update failed:', err));
    } else {
      console.log('🎯 No cart count elements found - check selectors');
    }
  }

  /**
   * Open cart drawer - Directly interact with cart-drawer-component
   */
  openCartDrawer() {
    // Method 1: Find the cart-drawer-component and call its open method
    const cartDrawer = document.querySelector('.cart-drawer, cart-drawer-component');
    
    if (cartDrawer) {
      // Try calling open() method if it exists
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }
      
      // Try setting open attribute/property
      if ('open' in cartDrawer) {
        cartDrawer.open = true;
        return;
      }
      
      // Try setting attribute and dispatching event
      cartDrawer.setAttribute('open', 'true');
      cartDrawer.dispatchEvent(new CustomEvent('open', { bubbles: true }));
      return;
    }
    
    // Method 2: Fallback - try clicking cart icon
    const cartSelectors = [
      '.header__icon--cart',
      '.cart-icon',
      'button[aria-label*="Cart"]',
      'a[href="/cart"]',
      '.cart-count:not(.cart-bubble__text-count)',
      '[data-cart-count]:not(.cart-bubble__text-count)'
    ];
    
    for (const selector of cartSelectors) {
      const cartElement = document.querySelector(selector);
      if (cartElement) {
        cartElement.click();
        return;
      }
    }

    // Method 3: Dispatch global cart events as last resort
    document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
  }

  /**
   * Initialize accessibility features
   */
  initAccessibility() {
    // Add ARIA labels to gallery
    if (this.gallery) {
      this.gallery.setAttribute('role', 'region');
      this.gallery.setAttribute('aria-label', 'Product image gallery');
    }

    // Ensure all interactive elements are keyboard accessible
    const buttons = this.container.querySelectorAll('.flint-hero__cta');
    buttons.forEach(button => {
      if (!button.hasAttribute('tabindex')) {
        button.setAttribute('tabindex', '0');
      }
    });

    // Add keyboard support for swatches
    const swatchLabels = this.container.querySelectorAll('.flint-hero__swatch-label');
    swatchLabels.forEach(label => {
      label.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          label.click();
        }
      });
    });
  }

  /**
   * Update main gallery image (when variant changes)
   */
  updateMainImage(imageUrl) {
    if (!this.flickityInstance) return;
    
    // Find the cell with this image
    const cells = this.gallery.querySelectorAll('.flint-hero__gallery-cell');
    let targetIndex = 0;
    
    cells.forEach((cell, index) => {
      const img = cell.querySelector('.flint-hero__gallery-image');
      if (img && img.src.includes(imageUrl)) {
        targetIndex = index;
      }
    });
    
    // Navigate to that image
    this.flickityInstance.select(targetIndex);
  }

  /**
   * Destroy instance (cleanup)
   */
  destroy() {
    if (this.flickityInstance) {
      this.flickityInstance.destroy();
    }
  }


}

// Removed complex global form interceptor - keeping it simple like AutoMazen

/**
 * Initialize all Flint Product Hero sections on the page
 */
document.addEventListener('DOMContentLoaded', () => {
  const heroSections = document.querySelectorAll('.flint-product-hero');
  
  console.log('Found hero sections:', heroSections.length);
  
  heroSections.forEach((section, index) => {
    console.log(`Initializing hero section ${index + 1}:`, section);
    new FlintProductHero(section);
  });
});

/**
 * Handle Shopify section editor events (Theme Customizer)
 */
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.flint-product-hero');
    if (section) {
      new FlintProductHero(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.flint-product-hero');
    if (section && section.flintHeroInstance) {
      section.flintHeroInstance.destroy();
    }
  });
}

/**
 * Lazy loading optimization
 * Preload images for better UX
 */
if ('IntersectionObserver' in window) {
  const imageObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
        imageObserver.unobserve(img);
      }
    });
  });

  // Observe lazy images
  document.querySelectorAll('.flint-hero__gallery-image[data-src]').forEach(img => {
    imageObserver.observe(img);
  });
}

/**
 * Performance: Debounce resize events
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Handle responsive adjustments
 */
const handleResize = debounce(() => {
  const heroSections = document.querySelectorAll('.flint-product-hero');
  heroSections.forEach(section => {
    // Recalculate heights if needed
    const gallery = section.querySelector('.flint-hero__gallery-container');
    if (gallery) {
      const viewportHeight = window.innerHeight;
      // Adjust gallery height if needed (CSS handles this, but JS can override if necessary)
    }
  });
}, 250);

window.addEventListener('resize', handleResize);

/**
 * Smooth scroll to info section (optional enhancement)
 */
const addScrollIndicator = () => {
  const heroSections = document.querySelectorAll('.flint-product-hero');
  
  heroSections.forEach(section => {
    const gallery = section.querySelector('.flint-hero__gallery-container');
    const info = section.querySelector('.flint-hero__info-container');
    
    if (gallery && info) {
      // Add a subtle scroll indicator
      const indicator = document.createElement('button');
      indicator.className = 'flint-hero__scroll-indicator';
      indicator.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 5v14m0 0l7-7m-7 7l-7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
      indicator.setAttribute('aria-label', 'Scroll to product details');
      
      indicator.addEventListener('click', () => {
        info.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      
      gallery.appendChild(indicator);
    }
  });
};

// Initialize scroll indicator (optional)
// Uncomment if you want this feature
// document.addEventListener('DOMContentLoaded', addScrollIndicator);

/**
 * Section Anchor Links - Smooth scroll with header offset
 * Handles all in-page anchor links (e.g. #flint-product-configurator)
 */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('a[href^="#"]').forEach(link => {
    const href = link.getAttribute('href');
    // Only handle anchors that point to an ID on this page (not empty # or external)
    if (href.length > 1 && !href.startsWith('#/')) {
      link.addEventListener('click', function (e) {
        const targetId = this.getAttribute('href');
        const target = document.querySelector(targetId);

        if (target) {
          e.preventDefault();
          const headerOffset = 80; // Adjust for fixed/sticky header
          const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
          window.scrollTo({ top: targetPosition, behavior: 'smooth' });
        }
        // If target not found, let browser handle it normally (fallback)
      });
    }
  });
});
